import React from 'react';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'destructive';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  children: React.ReactNode;
}

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  disabled,
  children,
  style,
  ...props
}) => {
  const getSizeStyles = (): React.CSSProperties => {
    const sizes = {
      sm: { padding: '6px 12px', fontSize: '14px' },
      md: { padding: '8px 16px', fontSize: '16px' },
      lg: { padding: '12px 24px', fontSize: '18px' },
    };
    return sizes[size];
  };

  const getVariantStyles = (): React.CSSProperties => {
    const variants = {
      primary: {
        background: 'linear-gradient(135deg, var(--primary) 0%, var(--accent) 100%)',
        color: 'white',
        boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
      },
      secondary: {
        background: 'var(--muted)',
        color: 'var(--foreground)',
        border: '1px solid var(--border)',
      },
      destructive: {
        background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
        color: 'white',
        boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)',
      },
    };
    return variants[variant];
  };

  const baseStyles: React.CSSProperties = {
    fontWeight: 600,
    borderRadius: '8px',
    border: 'none',
    cursor: disabled || isLoading ? 'not-allowed' : 'pointer',
    opacity: disabled || isLoading ? 0.6 : 1,
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    ...getSizeStyles(),
    ...getVariantStyles(),
    ...style,
  };

  return (
    <button
      style={baseStyles}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading ? 'Carregando...' : children}
    </button>
  );
};

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
}

export const Input: React.FC<InputProps> = ({
  label,
  error,
  helperText,
  style,
  ...props
}) => {
  const containerStyles: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    width: '100%',
  };

  const labelStyles: React.CSSProperties = {
    fontSize: '14px',
    fontWeight: 600,
    color: 'var(--foreground)',
    marginBottom: '2px',
  };

  const inputStyles: React.CSSProperties = {
    padding: '12px 14px',
    border: `2px solid ${error ? 'var(--destructive)' : 'var(--border)'}`,
    borderRadius: '8px',
    background: 'var(--input)',
    color: 'var(--foreground)',
    fontSize: '15px',
    outline: 'none',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    ...style,
  };

  const errorStyles: React.CSSProperties = {
    fontSize: '12px',
    color: 'var(--destructive)',
    marginTop: '2px',
  };

  const helperStyles: React.CSSProperties = {
    fontSize: '12px',
    color: 'var(--muted-foreground)',
    marginTop: '2px',
  };

  return (
    <div style={containerStyles}>
      {label && <label style={labelStyles}>{label}</label>}
      <input style={inputStyles} {...props} />
      {error && <span style={errorStyles}>{error}</span>}
      {!error && helperText && <span style={helperStyles}>{helperText}</span>}
    </div>
  );
};

export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  options: Array<{ value: string | number; label: string }>;
}

export const Select: React.FC<SelectProps> = ({
  label,
  error,
  options,
  style,
  ...props
}) => {
  const containerStyles: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    width: '100%',
  };

  const labelStyles: React.CSSProperties = {
    fontSize: '14px',
    fontWeight: 500,
    color: 'var(--foreground)',
  };

  const selectStyles: React.CSSProperties = {
    padding: '12px 14px',
    border: `2px solid ${error ? 'var(--destructive)' : 'var(--border)'}`,
    borderRadius: '8px',
    background: 'var(--input)',
    color: 'var(--foreground)',
    fontSize: '15px',
    outline: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    ...style,
  };

  const errorStyles: React.CSSProperties = {
    fontSize: '12px',
    color: 'var(--destructive)',
  };

  return (
    <div style={containerStyles}>
      {label && <label style={labelStyles}>{label}</label>}
      <select style={selectStyles} {...props}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {error && <span style={errorStyles}>{error}</span>}
    </div>
  );
};
